import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save } from 'lucide-react'
import { api } from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'
import FieldEditor from '../components/FieldEditor'

export default function FormBuilder() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    schema: { fields: [] },
    settings: {
      submit_button_text: 'Submit',
      success_message: 'Thank you for your submission!',
      notification_email: '',
    },
    is_active: true,
    sort_order: 0,
  })

  const { data: formRes, isLoading } = useQuery({
    queryKey: ['form', id],
    queryFn: () => api.get(`/forms/${id}`).then((res) => res.data),
    enabled: !!id,
  })

  const updateFormMutation = useMutation({
    mutationFn: (data) => api.put(`/forms/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['forms'])
      queryClient.invalidateQueries(['form', id])
      alert('Form updated successfully!')
    },
    onError: () => {
      alert('Failed to update form.')
    },
  })

  useEffect(() => {
    if (formRes?.data) {
      const form = formRes.data
      setFormData({
        name: form.name || '',
        slug: form.slug || '',
        schema: form.schema || { fields: [] },
        settings: {
          submit_button_text: form.settings?.submit_button_text || 'Submit',
          success_message:
            form.settings?.success_message ||
            'Thank you for your submission!',
          notification_email: form.settings?.notification_email || '',
        },
        is_active: form.is_active ?? true,
        sort_order: form.sort_order || 0,
      })
    }
  }, [formRes])

  const handleSubmit = (e) => {
    e.preventDefault()
    updateFormMutation.mutate(formData)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type !== 'submit') {
      e.preventDefault()
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSettingsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [field]: value },
    }))
  }

  const handleSchemaChange = (schema) => {
    setFormData((prev) => ({ ...prev, schema }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="secondary" onClick={() => navigate('/forms')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Form</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure form fields and settings.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="space-y-6"
      >
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Form name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Slug *
              </label>
              <Input
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                placeholder="form-slug"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sort Order
              </label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  handleInputChange('sort_order', parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange('is_active', e.target.checked)
                  }
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h3 className="text-lg font-medium text-gray-900">Form Settings</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Submit Button Text
              </label>
              <Input
                value={formData.settings.submit_button_text}
                onChange={(e) =>
                  handleSettingsChange('submit_button_text', e.target.value)
                }
                placeholder="Submit"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notification Email
              </label>
              <Input
                type="email"
                value={formData.settings.notification_email}
                onChange={(e) =>
                  handleSettingsChange('notification_email', e.target.value)
                }
                placeholder="admin@example.com"
              />
              <p className="mt-1 text-xs text-gray-500">
                Email to receive form submissions
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Success Message
            </label>
            <textarea
              value={formData.settings.success_message}
              onChange={(e) =>
                handleSettingsChange('success_message', e.target.value)
              }
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Thank you for your submission!"
            />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <FieldEditor schema={formData.schema} onChange={handleSchemaChange} />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/forms')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={updateFormMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {updateFormMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}

